import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { adminApi } from '../../services/adminApi';

interface Permission {
  id: number;
  name: string;
  description: string;
}

const Permissions: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [highlightedPermissionId, setHighlightedPermissionId] = useState<number | null>(null);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      console.log('Fetching permissions...');
      const response = await adminApi.getPermissions();
      console.log('Permissions response:', response);
      if (response.success) {
        console.log('Setting permissions:', response.data);
        setPermissions(response.data);
        
        // Check if we need to highlight a specific permission (navigated from Groups)
        const highlightPermissionId = sessionStorage.getItem('highlightPermissionId');
        if (highlightPermissionId) {
          const permissionId = parseInt(highlightPermissionId);
          setHighlightedPermissionId(permissionId);
          // Clear the sessionStorage
          sessionStorage.removeItem('highlightPermissionId');
          
          // Show success message to indicate navigation
          const permission = response.data.find(p => p.id === permissionId);
          if (permission) {
            setSuccess(`Navigated from Groups - Redaction Restriction "${permission.name}" is highlighted`);
          }
        }
      } else {
        console.error('Failed to fetch permissions:', response);
        setError('Failed to fetch permissions');
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('Error loading permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const handleOpenDialog = (permission?: Permission) => {
    if (permission) {
      setEditingPermission(permission);
      setFormData({ name: permission.name, description: permission.description });
    } else {
      setEditingPermission(null);
      setFormData({ name: '', description: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPermission(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async () => {
    try {
      if (editingPermission) {
        // Update permission
        const response = await adminApi.updatePermission(editingPermission.id, formData);
        if (response.success) {
          setPermissions(permissions.map(p => p.id === editingPermission.id ? response.data : p));
          handleCloseDialog();
          setSuccess('Redaction Restriction updated successfully');
        } else {
          setError('Failed to update Redaction Restriction');
        }
      } else {
        // Create permission
        const response = await adminApi.createPermission(formData);
        if (response.success) {
          setPermissions([...permissions, response.data]);
          handleCloseDialog();
          setSuccess('Redaction Restriction created successfully');
        } else {
          setError('Failed to create Redaction Restriction');
        }
      }
    } catch (err) {
      setError('Error saving Redaction Restriction');
    }
  };

  const handleDelete = async (permissionId: number) => {
    if (window.confirm('Are you sure you want to delete this Redaction Restriction?')) {
      try {
        const response = await adminApi.deletePermission(permissionId);
        if (response.success) {
          setPermissions(permissions.filter(p => p.id !== permissionId));
          setSuccess('Redaction Restriction deleted successfully');
        } else {
          setError('Failed to delete Redaction Restriction');
        }
      } catch (err) {
        setError('Error deleting Redaction Restriction');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Redaction Restrictions Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Add Redaction Restriction
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map((permission) => (
                <TableRow 
                  key={permission.id} 
                  hover
                  sx={{
                    backgroundColor: highlightedPermissionId === permission.id ? 'warning.light' : 'inherit',
                    '&:hover': {
                      backgroundColor: highlightedPermissionId === permission.id 
                        ? 'warning.main' 
                        : 'action.hover'
                    }
                  }}
                >
                  <TableCell>
                    <Chip 
                      label={permission.id} 
                      size="small" 
                      color={highlightedPermissionId === permission.id ? "warning" : "primary"} 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon 
                        color={highlightedPermissionId === permission.id ? "warning" : "action"} 
                        fontSize="small" 
                      />
                      <Typography 
                        sx={{ 
                          fontWeight: highlightedPermissionId === permission.id ? 'bold' : 'normal',
                          color: highlightedPermissionId === permission.id ? 'warning.dark' : 'inherit'
                        }}
                      >
                        {permission.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={highlightedPermissionId === permission.id ? "warning.dark" : "text.secondary"} 
                      sx={{ maxWidth: 300 }}
                    >
                      {permission.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(permission)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(permission.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPermission ? 'Edit Redaction Restriction' : 'Add New Redaction Restriction'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Redaction Restriction Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPermission ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Permissions; 